/**
 * Turn P — Wingman external briefs viewer.
 *
 * Renders POST /api/wingman-briefs feeds side-by-side with the Hail Mary
 * briefs page. Strictly isolated:
 *  - Fetches only /api/wingman-briefs and /api/wingman-briefs/by-date/*
 *  - No mutation, no re-trigger, no shared state with the Hail Mary pipeline
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Info, Link as LinkIcon, Newspaper } from "lucide-react";
import { API, authHeaders } from "@/lib/api";
import { toast } from "sonner";

const REGION_ORDER = ["global", "americas", "europe", "middle_east", "asia", "oceania"];
const REGION_LABEL = {
  global: "GLOBAL",
  americas: "AMERICAS",
  europe: "EUROPE",
  middle_east: "MIDDLE EAST",
  asia: "ASIA",
  oceania: "OCEANIA",
};

const WEBHOOK_URL = `${API}/webhooks/wingman/daily-brief`;

const SAMPLE_PAYLOAD = `{
  "brief_date": "YYYY-MM-DD",
  "generated_at": "2026-08-11T04:15:00Z",
  "source": "wingman",
  "regions": {
    "global":      [ Article, ... ],
    "americas":    [ Article, ... ],
    "asia":        [ Article, ... ],
    "europe":      [ Article, ... ],
    "middle_east": [ Article, ... ],
    "oceania":     [ Article, ... ]
  }
}

// Article =
{
  "title":       "string, required, non-empty",
  "summary":     "string, required, non-empty",
  "companies":   ["Hilton", "HOVR"],
  "topics":      ["M&A", "wellness"],
  "source_name": "Skift",
  "source_url":  "https://..."
}`;

function CopyButton({ text, label }) {
  return (
    <button
      type="button"
      data-testid={`wingman-copy-${label}`}
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => toast.success(`${label} copied`),
          () => toast.error("Copy failed"),
        );
      }}
      className="inline-flex items-center gap-1.5 rounded border border-hm-border bg-hm-bg-1 px-2 py-1 mono text-[10px] uppercase tracking-widest text-hm-fg-muted hover:bg-hm-bg-2 transition-colors"
    >
      <Copy className="h-3 w-3" />
      Copy
    </button>
  );
}

function Pill({ children, tone = "default" }) {
  const cls = tone === "accent"
    ? "bg-hm-accent-soft text-hm-accent-dim"
    : "bg-hm-bg-1 text-hm-fg-muted border border-hm-border";
  return (
    <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${cls}`}>
      {children}
    </span>
  );
}

function ArticleCard({ a, idx }) {
  return (
    <article
      data-testid={`wingman-article-${idx}`}
      className="rounded-md border border-hm-border bg-hm-bg-1 p-4 flex gap-3"
    >
      {/* Turn AO — thumbnail (96px) with graceful fallback to the icon
          when image_url is null OR loading fails. Aligned with the
          Entity detail page card so both surfaces feel like one product. */}
      <div
        data-testid={`wingman-article-${idx}-thumb`}
        className="shrink-0 rounded-md overflow-hidden grid place-items-center bg-hm-accent-soft"
        style={{ width: 96, height: 96 }}
      >
        {a.image_url
          ? <img
              src={a.image_url}
              alt=""
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                // On broken image, hide the <img> and reveal the sibling icon.
                e.currentTarget.style.display = "none";
                if (e.currentTarget.nextElementSibling) {
                  e.currentTarget.nextElementSibling.style.display = "grid";
                }
              }}
            />
          : null}
        {/* Fallback icon — hidden by default when image_url is present,
            shown by the onError handler above or when image_url is null. */}
        <Newspaper
          className="h-7 w-7 text-hm-accent-dim"
          style={{ display: a.image_url ? "none" : "grid", opacity: 0.5 }}
          aria-hidden
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <h3 className="text-hm-fg font-semibold leading-snug" style={{ fontSize: 15 }}>
          {a.title}
        </h3>
        <p className="text-hm-fg-muted leading-relaxed line-clamp-3" style={{ fontSize: 13 }}>
          {a.summary}
        </p>
        {(a.companies?.length > 0 || a.topics?.length > 0) && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {a.companies?.map((c) => <Pill key={`c-${c}`} tone="accent">{c}</Pill>)}
            {a.topics?.map((t) => <Pill key={`t-${t}`}>{t}</Pill>)}
          </div>
        )}
        <a
          href={a.source_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`wingman-article-${idx}-source`}
          className="inline-flex items-center gap-1.5 text-hm-accent-dim hover:text-hm-accent transition-colors mt-auto mono text-[11px] self-start"
        >
          <LinkIcon className="h-3 w-3" />
          {a.source_name}
        </a>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="wingman-empty"
      className="hm-surface p-8 flex flex-col gap-6"
    >
      <div className="text-center">
        <Info className="h-8 w-8 text-hm-fg-subtle mx-auto mb-3" />
        <div className="text-hm-fg font-medium">
          No Wingman brief received yet.
        </div>
        <p className="text-sm text-hm-fg-muted mt-2 max-w-md mx-auto">
          Configure the Wingman agent to POST its daily brief to the webhook below.
          Received briefs will appear here immediately.
        </p>
      </div>

      <div className="rounded-md border border-hm-border bg-hm-bg-1 p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            Webhook URL
          </div>
          <CopyButton text={WEBHOOK_URL} label="webhook-url" />
        </div>
        <code
          data-testid="wingman-webhook-url"
          className="mono text-xs text-hm-fg break-all bg-hm-bg-2 rounded px-2 py-1.5"
        >
          {WEBHOOK_URL}
        </code>
        <div className="text-[11px] text-hm-fg-muted">
          Required header: <code className="mono">Authorization: Bearer &lt;token&gt;</code>.
          Ask Nicolas for the token — it is not shown in the UI.
        </div>
      </div>

      <details className="rounded-md border border-hm-border bg-hm-bg-1 p-4">
        <summary className="cursor-pointer mono text-[10px] uppercase tracking-widest text-hm-fg-muted flex items-center justify-between">
          Expected JSON body
          <CopyButton text={SAMPLE_PAYLOAD} label="sample-payload" />
        </summary>
        <pre className="mono text-[11px] text-hm-fg-muted mt-3 whitespace-pre-wrap break-all">
{SAMPLE_PAYLOAD}
        </pre>
      </details>
    </div>
  );
}

function BriefSelector({ briefs, selectedId, onSelect }) {
  return (
    <div
      className="hm-surface p-3 flex flex-wrap gap-2"
      data-testid="wingman-brief-selector"
    >
      {briefs.map((b) => {
        const active = b.id === selectedId;
        return (
          <button
            key={b.id}
            type="button"
            data-testid={`wingman-brief-btn-${b.id}`}
            onClick={() => onSelect(b.id)}
            className={`rounded-md px-3 py-2 text-left transition-colors border ${
              active
                ? "bg-hm-accent-soft border-hm-accent-dim"
                : "bg-hm-bg-1 border-hm-border hover:bg-hm-bg-2"
            }`}
          >
            <div className="mono text-xs text-hm-fg">{b.brief_date}</div>
            <div className="mono text-[10px] text-hm-fg-muted mt-0.5">
              {new Date(b.received_at).toLocaleString()} · {b.article_count} art.
            </div>
          </button>
        );
      })}
    </div>
  );
}

function BriefBody({ brief }) {
  if (!brief) return null;
  const regions = brief.regions || {};
  return (
    <div className="flex flex-col gap-8 mt-6" data-testid="wingman-brief-body">
      <div className="flex items-baseline justify-between border-b border-hm-border pb-2">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
            Wingman · {brief.brief_date}
          </div>
          <div className="mono text-[11px] text-hm-fg-muted mt-1">
            Generated {brief.generated_at ? new Date(brief.generated_at).toLocaleString() : "—"}
            {" · "}Received {new Date(brief.received_at).toLocaleString()}
            {" · "}<span data-testid="wingman-brief-count">{brief.article_count} articles</span>
          </div>
        </div>
      </div>

      {REGION_ORDER.map((r) => {
        const items = regions[r] || [];
        if (items.length === 0) return null;
        return (
          <section
            key={r}
            data-testid={`wingman-region-${r}`}
            className="flex flex-col gap-3"
          >
            <h2
              className="mono uppercase tracking-widest text-hm-accent-dim"
              style={{ fontSize: 11, letterSpacing: "0.18em" }}
            >
              {REGION_LABEL[r]} · {items.length}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((a, i) => (
                <ArticleCard key={`${r}-${i}`} a={a} idx={`${r}-${i}`} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default function WingmanBriefs() {
  const { data: listData, isPending: listPending } = useQuery({
    queryKey: ["wingman-briefs"],
    queryFn: async () => {
      const res = await fetch(`${API}/wingman-briefs`, {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const briefs = listData?.data || [];
  const [selectedId, setSelectedId] = useState(null);

  const currentId = selectedId || briefs[0]?.id || null;
  const selectedDate = useMemo(
    () => briefs.find((b) => b.id === currentId)?.brief_date,
    [briefs, currentId],
  );

  const { data: dateData } = useQuery({
    queryKey: ["wingman-brief-by-date", selectedDate],
    enabled: !!selectedDate,
    queryFn: async () => {
      const res = await fetch(
        `${API}/wingman-briefs/by-date/${selectedDate}`,
        { headers: authHeaders(), credentials: "include" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const currentBrief = dateData?.data?.find((b) => b.id === currentId)
    || dateData?.data?.[0];

  if (listPending) {
    return (
      <div className="hm-surface p-10 text-center" data-testid="wingman-loading">
        <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
          Loading Wingman briefs…
        </div>
      </div>
    );
  }

  if (briefs.length === 0) {
    return <EmptyState />;
  }

  return (
    <div data-testid="wingman-briefs" className="flex flex-col gap-4">
      <BriefSelector
        briefs={briefs}
        selectedId={currentId}
        onSelect={setSelectedId}
      />
      <BriefBody brief={currentBrief} />
    </div>
  );
}
